import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import api, { API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Check, UploadCloud, Sparkles, ArrowRight, Palette } from "lucide-react";

const STEPS = ["Accent color", "Logo", "Paperwork"];

export default function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [settings, setSettings] = useState(null);
  const [accent, setAccent] = useState("#0F52BA");
  const [busy, setBusy] = useState(false);
  const [logoBust, setLogoBust] = useState(Date.now());
  const logoInput = useRef();
  const pdfInput = useRef();

  useEffect(() => {
    api.get("/clinic/settings").then((r) => { setSettings(r.data); setAccent(r.data.accent_color); }).catch((e) => console.debug("load settings failed:", e?.response?.status));
  }, []);

  const saveAccent = async (c) => { setAccent(c); try { await api.put("/clinic/branding", { accent_color: c }); } catch (e) { console.debug("save accent failed:", e?.response?.status); } };

  const onLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try { await api.post("/clinic/logo", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Logo uploaded."); setSettings((s) => ({ ...s, has_logo: true })); setLogoBust(Date.now()); }
    catch (e2) { toast.error(formatApiError(e2.response?.data?.detail) || "Upload failed."); }
    finally { setBusy(false); }
  };

  const onPdf = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try { const r = await api.post("/clinic/template/from-pdf", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`Form built — ${r.data.steps.length} sections.`); }
    catch (e2) { toast.error(formatApiError(e2.response?.data?.detail) || "Could not build form."); }
    finally { setBusy(false); }
  };

  const finish = async () => {
    try { await api.post("/clinic/onboarding/complete", {}); } catch (e) { console.debug("onboarding complete failed:", e?.response?.status); }
    toast.success("Setup complete!");
    navigate("/admin");
  };

  const presets = settings?.accent_presets || ["#0F52BA", "#0E7C66", "#7C3AED", "#B4245A", "#C2410C", "#0891B2", "#4338CA", "#334155"];

  return (
    <div className="min-h-screen bg-slate-50">
      <SiteHeader />
      <div className="mx-auto max-w-xl px-4 py-10 sm:px-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Welcome — let's set up your Nimbus</h1>
        <p className="mt-1 text-sm text-slate-500">Three quick steps. You can change any of this later in Settings.</p>

        <div className="mt-6 flex gap-2">
          {STEPS.map((s, i) => (
            <div key={s} className="flex-1">
              <div className="h-1.5 rounded-full" style={{ backgroundColor: i <= step ? accent : "#e2e8f0" }} />
              <p className={`mt-1 text-xs ${i === step ? "font-semibold text-slate-700" : "text-slate-400"}`}>{s}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6" data-testid="onboarding-card">
          {step === 0 && (
            <>
              <div className="flex items-center gap-2"><Palette className="h-4 w-4 text-slate-400" /><h2 className="font-semibold text-slate-900">Pick your accent color</h2></div>
              <div className="mt-4 flex flex-wrap gap-3">
                {presets.map((c) => (
                  <button key={c} onClick={() => saveAccent(c)} data-testid={`onb-accent-${c.replace('#','')}`} className="relative h-10 w-10 rounded-full transition-transform hover:scale-110" style={{ backgroundColor: c, boxShadow: accent === c ? `0 0 0 3px #fff, 0 0 0 5px ${c}` : "none" }}>
                    {accent === c && <Check className="absolute inset-0 m-auto h-4 w-4 text-white" />}
                  </button>
                ))}
              </div>
            </>
          )}
          {step === 1 && (
            <>
              <h2 className="font-semibold text-slate-900">Upload your logo</h2>
              <p className="mt-1 text-sm text-slate-500">Optional — shown in your portal and PDF.</p>
              <div className="mt-4 flex items-center gap-4">
                {settings?.has_logo && <img src={`${API}/clinic/logo/${settings.clinic_id}?t=${logoBust}`} alt="logo" className="h-14 w-auto max-w-[160px] rounded-lg border border-slate-200 object-contain p-1" />}
                <input ref={logoInput} type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="onb-logo-input" />
                <Button variant="outline" onClick={() => logoInput.current?.click()} disabled={busy} data-testid="onb-logo-btn">
                  {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <UploadCloud className="mr-1.5 h-4 w-4" />} Upload logo
                </Button>
              </div>
            </>
          )}
          {step === 2 && (
            <>
              <h2 className="font-semibold text-slate-900">Add your paperwork</h2>
              <p className="mt-1 text-sm text-slate-500">Upload a fillable PDF and we'll build your intake form with AI. Or keep the starter form and edit it later.</p>
              <input ref={pdfInput} type="file" accept="application/pdf" className="hidden" onChange={onPdf} data-testid="onb-pdf-input" />
              <Button className="mt-4 text-white" style={{ backgroundColor: accent }} onClick={() => pdfInput.current?.click()} disabled={busy} data-testid="onb-pdf-btn">
                {busy ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Building form…</> : <><Sparkles className="mr-1.5 h-4 w-4" /> Upload fillable PDF</>}
              </Button>
            </>
          )}

          <div className="mt-6 flex items-center justify-between">
            <Button variant="ghost" disabled={step === 0 || busy} onClick={() => setStep((s) => s - 1)}>Back</Button>
            {step < STEPS.length - 1 ? (
              <Button className="text-white" style={{ backgroundColor: accent }} onClick={() => setStep((s) => s + 1)} disabled={busy} data-testid="onb-next">Continue <ArrowRight className="ml-1 h-4 w-4" /></Button>
            ) : (
              <Button className="text-white" style={{ backgroundColor: accent }} onClick={finish} disabled={busy} data-testid="onb-finish">Finish setup <Check className="ml-1 h-4 w-4" /></Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
